import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import PropTypes from 'prop-types';

const Modal = ({ isOpen, onClose, title, children, modalWidth = 'sm' }) => {
    const widthClasses = {
        xs: 'w-full max-w-sm',
        sm: 'w-full max-w-lg',
        md: 'w-full max-w-2xl',
        lg: 'w-full max-w-3xl',
        xl: 'w-full max-w-4xl',
        '2xl': 'w-full max-w-5xl'
    };

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto ">
                    <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0 ">
                        {/* Backdrop */}
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-lg dark:bg-black/40" aria-hidden="true" />

                        {/* Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{
                                type: 'spring',
                                duration: 0.3,
                                bounce: 0.3
                            }}
                            className={`relative transform overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 ${widthClasses[modalWidth]} border-2 border-gray-400/50 dark:border-[#68D2F0]/20`}
                        >
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">{title}</h3>
                                    <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400">
                                        <span className="sr-only">Close</span>
                                        <X className="h-5 w-5" aria-hidden="true" />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="px-6 py-4">{children}</div>
                        </motion.div>
                    </div>
                </div>
            )}
        </AnimatePresence>
    );
};

Modal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    modalWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl'])
};

Modal.defaultProps = {
    modalWidth: 'sm'
};

export default Modal;
